/* eslint-disable max-lines-per-function */
import { ChangeEventHandler, forwardRef, useEffect, useState } from 'react';

import { FormHelperText, ErrorTextProps, FormHelperTextProps } from '../form-helper-text/form-helper-text';
import { RadioAccordion, RadioAccordionProps } from '../radio-accordion/radio-accordion';
import { RadioButton, RadioButtonProperties, RadioButtonSize } from '../radio-button/radio-button';
import { RadioInput, RadioInputProps } from '../radio-button/radio-input';

export type RadioButtonGroupItem = RadioButtonProperties | RadioAccordionProps | Partial<RadioInputProps>;

interface RadioButtonGroupErrorProps {
  error?: string;
  errorProps?: ErrorTextProps;
  buttonSize?: RadioButtonSize;
}

const RadioButtonGroupError = (props: RadioButtonGroupErrorProps) => {
  const { buttonSize, error, errorProps } = props;
  const newProps = errorProps ?? { display: error, visible: true };
  const formHelperTextProps: FormHelperTextProps = {
    ...newProps,
    type: 'error',
    size: errorProps?.size ?? buttonSize ?? 'large',
    className: errorProps?.className,
  };
  return <FormHelperText {...formHelperTextProps} />;
};

export interface RadioButtonGroupProperties {
  /** The items to include in the button group. */
  data: RadioButtonGroupItem[];
  /** The (HTML) name of the radio button group */
  name: string;
  /** The orientation of the list */
  layout: 'horizontal' | 'vertical';
  /** The controlled value of the button group */
  value?: string;
  /** Event handler to return the new value */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /** The optional label for the radio button group */
  label?: string;
  /** DEPRECATED. use errorProps instead */
  error?: string;
  /** Props to use for the FormHelperText */
  errorProps?: ErrorTextProps;
  /** Event handler to return the new value and the selected checkboxes */
  onValueChange?: (radioValue: string, selectedBoxes: string[]) => void;
  /** The size of the button */
  buttonSize?: RadioButtonSize;
}

const options = {
  layout: {
    vertical: 'flex flex-col',
    horizontal: 'flex flex-row gap-x-8',
  },
  margin: {
    medium: 'mt-1',
    large: 'mt-2',
  },
};

const defaultFunction = () => {
  return;
};

const updateCurrentValue = (radioValue: string): ((current: string) => string) => {
  return (current: string) => {
    return current === radioValue ? '' : current;
  };
};

export const RadioButtonGroup = forwardRef<HTMLInputElement, RadioButtonGroupProperties>(
  ({ layout = 'vertical', ...props }, ref) => {
    const {
      label,
      data,
      onChange = defaultFunction,
      onValueChange = defaultFunction,
      name,
      error,
      errorProps,
      value: propValue = '',
      buttonSize,
    } = props;
    const [value, setValue] = useState(propValue);

    const onRadioChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      setValue(e.target.value);
      onChange(e);
      onValueChange(e.target.value, []);
    };

    const onCheckboxChange = (radioValue: string, selectedBoxes: string[]) => {
      if (selectedBoxes.length === 0) {
        setValue(updateCurrentValue(radioValue));
      } else {
        setValue(radioValue);
      }
      onValueChange(radioValue, selectedBoxes);
    };

    useEffect(() => {
      setValue(propValue);
    }, [propValue]);

    const itemWrapperClasses = buttonSize === 'medium' ? 'p-1' : 'p-2.5';

    const defaultMargin = error || errorProps?.visible ? '' : options.margin[buttonSize || 'large'];

    return (
      <fieldset>
        {label && (
          <div className="flex flex-col">
            <legend className="body-l">{label}</legend>
          </div>
        )}
        {(error || errorProps) && (
          <RadioButtonGroupError error={error} errorProps={errorProps} buttonSize={buttonSize} />
        )}
        <div className={`${options.layout[layout]} ${defaultMargin}`}>
          {data.map(({ name: id, ...radioButtonProps }, index) => {
            const radioChecked = value === radioButtonProps.value;
            if ('checkboxData' in radioButtonProps) {
              return (
                <div key={radioButtonProps.value} className={itemWrapperClasses}>
                  <RadioAccordion
                    {...radioButtonProps}
                    name={name}
                    checked={radioChecked}
                    onChange={onRadioChange}
                    onCheckboxChange={onCheckboxChange}
                    size={buttonSize}
                  />
                </div>
              );
            }
            if ('input1Props' in radioButtonProps) {
              return (
                <div key={radioButtonProps.value} className={itemWrapperClasses}>
                  <RadioInput
                    {...(radioButtonProps as RadioInputProps)}
                    name={name}
                    checked={radioChecked}
                    onChange={onRadioChange}
                    onValueChange={onCheckboxChange}
                    size={buttonSize}
                  />
                </div>
              );
            }
            return (
              <div key={radioButtonProps.value} className={itemWrapperClasses}>
                <RadioButton
                  {...(radioButtonProps as RadioButtonProperties)}
                  ref={index === 0 ? ref : undefined}
                  checked={radioChecked}
                  onChange={onRadioChange}
                  size={buttonSize}
                  name={name}
                  id={id}
                />
              </div>
            );
          })}
        </div>
      </fieldset>
    );
  }
);

RadioButtonGroup.displayName = 'RadioButtonGroup';

export default RadioButtonGroup;
